import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  ArrowLeft,
  ChevronRight,
  Home,
  DollarSign,
  Users,
  Building,
  MapPin
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import { Breadcrumb } from '../../components/Common'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import customerMasterService from '../../services/customerMasterService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const CustomerMaster = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalCustomers: 0,
    activeCustomers: 0,
    inactiveCustomers: 0,
    categoryStats: []
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      loadData()
      hasLoadedRef.current = true
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [customersResponse, statsResponse] = await Promise.all([
        customerMasterService.getAllCustomers({ sortBy: 'customerCode', sortOrder: 'desc' }),
        customerMasterService.getCustomerStats()
      ])

      if (customersResponse.success) {
        const customers = customersResponse.data || []
        setData(customers)
        if (customers.length === 0 && !hasShownEmptyToastRef.current) {
          toast.info('No customer records found. Create your first customer!')
          hasShownEmptyToastRef.current = true
        }
      } else {
        toast.error(customersResponse.message || 'Failed to load customer records')
        setData([])
      }

      if (statsResponse.success) {
        setStats(statsResponse.data || {
          totalCustomers: 0,
          activeCustomers: 0,
          inactiveCustomers: 0,
          categoryStats: []
        })
      } else {
        console.error('Failed to load customer stats:', statsResponse.message)
      }
    } catch (error) {
      console.error('Error loading customer data:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
        navigate('/login')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view customer records')
      } else {
        toast.error('Failed to load customer records')
      }
      setData([])
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      accessorKey: 'customerCode',
      header: 'Customer Code',
      size: 120,
      cell: ({ getValue }) => (
        <div className="sac-code-cell">
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'customerName',
      header: 'Customer Name',
      size: 200,
      cell: ({ getValue }) => (
        <div className="description-cell">
          <span className="description-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'customerCategory',
      header: 'Category',
      size: 150,
      cell: ({ getValue }) => (
        <div className="rate-cell">
          <span className="rate-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'region',
      header: 'Region',
      size: 120,
      cell: ({ getValue }) => (
        <div className="rate-cell">
          <span className="rate-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'customerContactInfo',
      header: 'Contact Person',
      size: 150,
      cell: ({ getValue }) => {
        const contactInfo = getValue()
        const primaryContact = contactInfo && contactInfo.length > 0 ? contactInfo[0] : null
        return (
          <div className="rate-cell">
            <span className="rate-text">{primaryContact?.contactPersonName || 'N/A'}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'GSTIN',
      header: 'GSTIN',
      size: 150,
      cell: ({ getValue }) => (
        <div className="rate-cell">
          <span className="rate-text">{getValue() || 'N/A'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'isCustomerActive',
      header: 'Status',
      size: 100,
      cell: ({ getValue }) => {
        const status = getValue() === 'A' ? 'Active' : 'Inactive'
        return (
          <div className="status-cell">
            <span className={`status-badge ${status.toLowerCase()}`}>
              {status}
            </span>
          </div>
        )
      },
    },
  ]

  const handleBack = () => {
    navigate('/sales')
  }

  const handleAddNew = () => {
    navigate('/sales/customer-master/form')
  }

  const handleExport = () => {
    // Handle export
    console.log('Export Customer Master data')
  }

  const handleEdit = (customerData) => {
    // Get the customer ID - check both _id and id fields
    const customerId = customerData._id || customerData.id
    
    if (!customerId) {
      toast.error('Cannot edit: Customer ID is missing')
      return
    }
    
    // Navigate with ID in URL and data in state
    navigate(`/sales/customer-master/edit/${customerId}`, { state: { customerData } })
  }

  const handleView = (customerData) => {
    navigate('/sales/customer-master/view', { state: { customerData } })
  }

  const handleDelete = (customerData) => {
    setDeleteItem(customerData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting customer...')
      const response = await customerMasterService.deleteCustomer(deleteItem._id)
      if (response.success) {
        toast.success('Customer deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete customer')
      }
    } catch (error) {
      console.error('Error deleting customer:', error)
      if (error.response?.status === 404) {
        toast.error('Customer not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this customer')
      } else {
        toast.error('Failed to delete customer. Please try again.')
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Sales',
      path: '/sales',
      onClick: () => navigate('/sales')
    },
    {
      label: 'Masters',
      path: '/sales?tab=masters',
      onClick: () => navigate('/sales?tab=masters')
    },
    {
      label: 'Customer Master',
      path: '/sales/customer-master',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Customer Master</h1>
          <p>Manage customer master data</p>
        </div>
        <div className="page-actions">
          <button className="btn-primary" onClick={handleAddNew}>
            <Plus size={18} />
            Add New
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalCustomers}</div>
          <div className="metric-title">Total Customers</div>
          <div className="metric-subtitle">All customer records</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Building size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeCustomers}</div>
          <div className="metric-title">Active Customers</div>
          <div className="metric-subtitle">Currently active</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Inactive</span>
            </div>
          </div>
          <div className="metric-value">{stats.inactiveCustomers}</div>
          <div className="metric-title">Inactive Customers</div>
          <div className="metric-subtitle">Disabled or archived</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <MapPin size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Categories</span>
            </div>
          </div>
          <div className="metric-value">{stats.categoryStats?.length || 0}</div>
          <div className="metric-title">Categories</div>
          <div className="metric-subtitle">Different customer categories</div>
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        data={data}
        columns={columns}
        loading={loading}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onExport={handleExport}
        noCard={true}
        className="customer-master-table"
        pageSize={10}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Customer"
        message={`Are you sure you want to delete "${deleteItem?.customerName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default CustomerMaster
